import React from 'react'
import styled from 'styled-components'
import Modal from '../modal'
import Button from '../button'
import LoadingButton from '../loading-button'
import TextField from '../text-field'
import TextArea from '../text-area'
import Select, { SelectOption } from '../select'
import RadioButton from '../radio-button'
import NotEditableIndicator from './not-editable-indicator'
import useLoanEntities from '../hooks/useLoanEntities'
import useDocumentForm from './useDocumentForm'
import useUpsertDocument from './useUpsertDocument'
import { CATEGORY_LABELS, PHASE_LABELS } from '../constants'
import {
  getIsOperatingCompany,
  getIsFinancialCategory,
  formatEntities,
} from './utils'
import {
  CATEGORIES_TO_SHOW,
  PHASES_TO_SHOW,
  NOT_FULL_WIDTH_INPUTS,
} from './constants'

const ContentWrapperElement = styled.form`
  width: fit-content;
  margin: 0 !important;
`

const SubtitleElement = styled.h4`
  font-size: 1rem !important;
  font-weight: 200 !important;
  margin-top: 1.2rem !important;
  margin-bottom: 0.8rem !important;
`

const ButtonsContainerElement = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1rem;
`

const RadioButtonsContainerElement = styled.div`
  display: flex;
  gap: 2rem;
`
const CategoryAndPhaseWrapperElement = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
`

const LabelAndInputWrapperElement = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`

function DocumentFormModal({ loanId, onClose, editingAttrs, creatingAttrs }) {
  const entities = formatEntities(useLoanEntities())

  const { onSubmit, loading } = useUpsertDocument({
    loanId,
    editingAttrs,
    creatingAttrs,
    onClose,
  })

  const {
    register,
    handleSubmit,
    entityField,
    phase,
    category,
    formState: { isDirty, errors },
  } = useDocumentForm({
    creatingAttrs,
    editingAttrs,
  })

  const isOperatingCompany = getIsOperatingCompany(entityField.value)
  const disableSubmit = loading || editingAttrs ? !isDirty : false
  const notEditable = editingAttrs && !editingAttrs.document.custom
  const hasFilesUploaded = Boolean(editingAttrs?.document.files.length > 0)
  const isFinancialCategory = getIsFinancialCategory(category)

  const handleClose = () => !loading && onClose()

  return (
    <Modal
      title={`${editingAttrs ? 'Edit' : 'New'} document bucket`}
      onClose={handleClose}
    >
      <ContentWrapperElement onSubmit={handleSubmit(onSubmit)}>
        <SubtitleElement>
          Document name {notEditable && <NotEditableIndicator />}
        </SubtitleElement>
        <TextField
          {...register('name', { required: 'Document name is required' })}
          error={Boolean(errors.name)}
          helperText={errors.name?.message || ''}
          width={NOT_FULL_WIDTH_INPUTS}
          disabled={notEditable}
        />
        <SubtitleElement>
          Document guidance for applicant{' '}
          {notEditable && <NotEditableIndicator />}
        </SubtitleElement>
        <TextArea
          {...register('description')}
          minHeight="10.75rem"
          minWidth="45rem"
          disabled={notEditable}
        />
        <SubtitleElement>
          Where does this document go? {notEditable && <NotEditableIndicator />}
          {!notEditable && hasFilesUploaded && (
            <NotEditableIndicator message="Document owner cannot be changed once files are uploaded" />
          )}
        </SubtitleElement>
        <Select
          {...entityField}
          width={NOT_FULL_WIDTH_INPUTS}
          disabled={notEditable || hasFilesUploaded}
        >
          {entities.map(({ customId, name }) => (
            <SelectOption key={customId} value={customId}>
              {name}
            </SelectOption>
          ))}
        </Select>
        <CategoryAndPhaseWrapperElement>
          {isOperatingCompany && (
            <LabelAndInputWrapperElement>
              <SubtitleElement>
                Category {notEditable && <NotEditableIndicator />}
              </SubtitleElement>
              <Select {...register('category')} disabled={notEditable}>
                {!CATEGORIES_TO_SHOW.includes(category) && (
                  <SelectOption key={category} value={category}>
                    {CATEGORY_LABELS[category]}
                  </SelectOption>
                )}
                {CATEGORIES_TO_SHOW.map((option) => (
                  <SelectOption key={option} value={option}>
                    {CATEGORY_LABELS[option]}
                  </SelectOption>
                ))}
              </Select>
            </LabelAndInputWrapperElement>
          )}
          {(!isOperatingCompany || isFinancialCategory) && (
            <LabelAndInputWrapperElement>
              <SubtitleElement>
                Phase {notEditable && <NotEditableIndicator />}
              </SubtitleElement>
              <Select {...register('phase')} disabled={notEditable}>
                {!PHASES_TO_SHOW.includes(phase) && (
                  <SelectOption key={phase} value={phase}>
                    {PHASE_LABELS[phase]}
                  </SelectOption>
                )}
                {PHASES_TO_SHOW.map((option) => (
                  <SelectOption key={option} value={option}>
                    {PHASE_LABELS[option]}
                  </SelectOption>
                ))}
              </Select>
            </LabelAndInputWrapperElement>
          )}
        </CategoryAndPhaseWrapperElement>
        <SubtitleElement>
          Display this bucket on the applicant's document upload page?
        </SubtitleElement>
        <RadioButtonsContainerElement>
          <RadioButton label="Yes" value="false" {...register('adminOnly')} />
          <RadioButton label="No" value="true" {...register('adminOnly')} />
        </RadioButtonsContainerElement>
        <SubtitleElement>
          Would you like to make this document available for eSignature?
        </SubtitleElement>
        <RadioButtonsContainerElement>
          <RadioButton label="Yes" value="true" {...register('esignable')} />
          <RadioButton label="No" value="false" {...register('esignable')} />
        </RadioButtonsContainerElement>
        <ButtonsContainerElement>
          <Button color="gray" variant="outlined" onClick={handleClose}>
            Cancel
          </Button>
          <LoadingButton
            type="submit"
            disabled={disableSubmit}
            isLoading={loading}
            width="5rem"
          >
            {editingAttrs ? 'Update' : 'Create'}
          </LoadingButton>
        </ButtonsContainerElement>
      </ContentWrapperElement>
    </Modal>
  )
}

export default DocumentFormModal
